import React, { useEffect, useState } from 'react';
import {
  Crown,
  Coins,
  MessageCircle,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  ArrowRight,
  ChevronLeft,
  CreditCard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TicketRewards } from '@/components/TicketRewards';
import { ReferralShareCard } from '@/components/ReferralShareCard';
import { getAuthHeaders } from '@/hooks/useAuth';
import { useSubscription, SUBSCRIPTION_TIERS } from '@/hooks/useSubscription';
import { useCredits, CreditPackage } from '@/hooks/useCredits';
import { CharterMemberRow } from '@/components/CharterBadge';
import { LoginModal } from '@/components/LoginModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ReaderAvatar } from '@/components/brand';
import { useNavigate } from 'react-router-dom';
import { openCommunity } from '@/config/community';

const AccountPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    subscription: _subscription,
    loading: subLoading,
    isSubscriber,
    cancelSubscription,
    reactivateSubscription,
    startSubscription,
    activateCreator,
    getStatusDisplay: _getStatusDisplay,
    getEndDateDisplay,
    getTierDisplay,
    readerSessionsRemaining,
    readerSessionsUsed,
    readerSessionsLimit,
    cancelAtPeriodEnd,
    fetchSubscription,
    tier,
    isDayPass,
    canBeReader,
    canUseTools,
    creditsIncluded: _creditsIncluded,
  } = useSubscription();

  const {
    balance,
    packages,
    fetchPackages,
    purchaseCredits,
    loading: _creditLoading,
  } = useCredits();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  // Ticket Rewards progress, so the cancel dialog can say exactly what they'd
  // walk away from — an unclaimed $25 code, or the months already banked toward
  // the next one — instead of a generic bullet.
  const [rewardsPeek, setRewardsPeek] = useState<{
    eligible: boolean; available: boolean; rewardValue: string; intervalMonths: number;
    membershipMonths: number; nextRewardInMonths: number | null; nextRewardDate: string | null;
  } | null>(null);
  useEffect(() => {
    if (!cancelOpen) return;
    fetch('/api/ticket-rewards', { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setRewardsPeek(d))
      .catch(() => setRewardsPeek(null));
  }, [cancelOpen]);
  // One plain sentence, Joe's wording.
  const rewardForfeitLine = rewardsPeek?.eligible
    ? `You'll lose your ${rewardsPeek.rewardValue || '$25'} Ticket Reward if you cancel.`
    : null;

  type DiscordStatus = {
    configured: boolean;
    linked: boolean;
    discordUsername: string | null;
    roles: { member?: boolean; creator?: boolean; reader?: boolean } | null;
    lastSyncedAt: string | null;
  };
  const [discord, setDiscord] = useState<DiscordStatus | null>(null);
  const [discordBusy, setDiscordBusy] = useState<string | null>(null);

  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  const storedUserExists = !!localStorage.getItem('castingUser');
  const isAuthenticated = !!(token || storedUserExists);

  let userEmail: string | null = null;
  let userName: string | null = null;
  const storedUser = localStorage.getItem('castingUser');
  if (storedUser) {
    try {
      const userData = JSON.parse(storedUser);
      userEmail = userData.email;
      userName = userData.first_name || userData.firstName || userData.name?.split(' ')[0];
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    if (isAuthenticated) fetchPackages();
  }, [isAuthenticated, fetchPackages]);

  const discordAuth = (): HeadersInit => (token ? { Authorization: `Bearer ${token}` } : {});
  const loadDiscord = React.useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/discord/status', { headers: discordAuth() });
      if (res.ok) setDiscord(await res.json());
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { if (isAuthenticated) loadDiscord(); }, [isAuthenticated, loadDiscord]);

  const connectDiscord = async () => {
    setDiscordBusy('connect');
    try {
      const res = await fetch('/api/discord/oauth/start', { headers: discordAuth() });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; return; }
    } catch { /* ignore */ }
    setDiscordBusy(null);
  };
  const resyncDiscord = async () => {
    setDiscordBusy('resync');
    try {
      await fetch('/api/discord/resync', { method: 'POST', headers: discordAuth() });
      await loadDiscord();
    } catch { /* ignore */ }
    setDiscordBusy(null);
  };
  const unlinkDiscord = async () => {
    if (!confirm('Disconnect Discord? Your community roles will no longer sync.')) return;
    setDiscordBusy('unlink');
    try {
      await fetch('/api/discord/unlink', { method: 'POST', headers: discordAuth() });
      await loadDiscord();
    } catch { /* ignore */ }
    setDiscordBusy(null);
  };

  const handleCancelSubscription = async () => {
    setCancelOpen(false);
    setActionLoading('cancel');
    try { await cancelSubscription(); }
    finally { setActionLoading(null); }
  };

  const handleReactivateSubscription = async () => {
    setActionLoading('reactivate');
    try { await reactivateSubscription(); }
    finally { setActionLoading(null); }
  };

  const handlePurchaseCredits = async (packageId: string) => {
    setActionLoading(`purchase-${packageId}`);
    try {
      const checkoutUrl = await purchaseCredits(packageId);
      if (checkoutUrl) window.location.href = checkoutUrl;
    } finally {
      setActionLoading(null);
    }
  };

  const openChatSupport = () => {
    if (window._chatlio && typeof window._chatlio.show === 'function') {
      try {
        window._chatlio.show({ expanded: true });
      } catch {
        window.open('mailto:hello@heyjoe.io', '_blank');
      }
    } else {
      window.open('mailto:hello@heyjoe.io', '_blank');
    }
  };

  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  if (!isAuthenticated) {
    return (
      <main
        className="mx-auto"
        style={{
          maxWidth: 1440,
          padding: '64px 16px',
          background: 'var(--hj-bg)',
          color: 'var(--hj-ink)',
          minHeight: 'calc(100vh - 68px)',
        }}
      >
        <div
          className="mx-auto text-center"
          style={{
            maxWidth: 440,
            padding: 36,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          <Crown
            className="mx-auto"
            size={36}
            strokeWidth={1.6}
            style={{ color: 'var(--hj-yellow-ink)', marginBottom: 12 }}
          />
          <h1
            className="font-hj-display m-0"
            style={{
              fontSize: 28,
              color: 'var(--hj-ink)',
              letterSpacing: '-0.01em',
            }}
          >
            Sign in required
          </h1>
          <p style={{ marginTop: 8, marginBottom: 20, color: 'var(--hj-ink)', fontSize: 14 }}>
            Sign in to manage your Hey Joe+ account.
          </p>
          <Button onClick={() => setShowLoginModal(true)} size="lg">Sign In</Button>
        </div>
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onSuccess={() => {
            setShowLoginModal(false);
            fetchSubscription();
          }}
        />
      </main>
    );
  }

  const statusPill = isSubscriber
    ? cancelAtPeriodEnd
      ? { color: 'var(--hj-yellow-ink)', bg: 'var(--hj-yellow-tint)', Icon: AlertCircle, label: 'Canceling' }
      : { color: 'var(--hj-green)', bg: 'oklch(0.42 0.15 145 / 0.18)', Icon: CheckCircle, label: 'Active' }
    : { color: 'var(--hj-ink-3)', bg: 'var(--hj-surface-2)', Icon: XCircle, label: 'Not subscribed' };

  return (
    <main
      className="mx-auto"
      style={{
        maxWidth: 1440,
        padding: '0 16px 96px',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      {/* Breadcrumb */}
      <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5"
          style={{
            background: 'transparent',
            border: 0,
            color: 'var(--hj-ink-3)',
            cursor: 'pointer',
            padding: 0,
            fontFamily: 'inherit',
            fontSize: 13,
          }}
        >
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </button>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>Account</span>
      </div>

      {/* Header */}
      <header
        className="flex items-center gap-4 sm:gap-6"
        style={{
          margin: '4px 0 0',
          padding: '22px 24px 20px',
          borderRadius: 'var(--hj-r-md)',
          background: 'var(--hj-beige)',
          border: '1px solid var(--hj-beige-line)',
          color: 'var(--hj-beige-ink)',
        }}
      >
        <ReaderAvatar name={userName || userEmail || 'You'} size={64} />
        <div className="min-w-0">
          <div className="mono-label" style={{ marginBottom: 6, color: 'var(--hj-beige-ink-2)' }}>Account</div>
          <h1
            className="font-hj-display m-0 truncate"
            style={{
              fontSize: 'clamp(28px, 4vw, 44px)',
              lineHeight: 1.05,
              color: 'var(--hj-beige-ink)',
              letterSpacing: '-0.01em',
            }}
          >
            {userName ? `Welcome back, ${userName}.` : 'My account.'}
          </h1>
          {userEmail && (
            <p
              className="m-0 truncate"
              style={{ marginTop: 6, color: 'var(--hj-beige-ink-2)', fontSize: 13 }}
            >
              {userEmail}
            </p>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ paddingTop: 24 }}>
        {/* Subscription Card — full width */}
        <section
          className="lg:col-span-2"
          style={{
            padding: 24,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="mono-label" style={{ marginBottom: 4 }}>Subscription</div>
              <h2
                className="font-hj-display m-0"
                style={{
                  fontSize: 22,
                  color: 'var(--hj-ink)',
                  letterSpacing: '-0.01em',
                }}
              >
                Hey Joe+ membership
              </h2>
              <p
                className="m-0"
                style={{ marginTop: 4, color: 'var(--hj-ink-3)', fontSize: 13 }}
              >
                Manage your subscription, payment, and renewal.
              </p>
            </div>
            <span
              className="inline-flex items-center gap-1.5 mono-label flex-shrink-0"
              style={{
                padding: '4px 10px',
                borderRadius: 999,
                background: statusPill.bg,
                color: statusPill.color,
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              <statusPill.Icon size={11} strokeWidth={2.2} />
              {statusPill.label}
            </span>
          </div>

          {subLoading ? (
            <div className="flex items-center justify-center" style={{ padding: '36px 0' }}>
              <RefreshCw className="w-5 h-5 animate-spin" style={{ color: 'var(--hj-ink-4)' }} />
            </div>
          ) : isSubscriber ? (
            <>
              {/* Stats row */}
              <div
                className="grid grid-cols-1 sm:grid-cols-3 gap-3"
                style={{ marginTop: 22 }}
              >
                <StatCard
                  label="Plan"
                  value={getTierDisplay()}
                  sub={
                    _subscription?.isUnlimited ? 'Admin granted · unlimited'
                    : tier && SUBSCRIPTION_TIERS[tier]?.priceDisplay ? (SUBSCRIPTION_TIERS[tier].period ? `${SUBSCRIPTION_TIERS[tier].priceDisplay}/${SUBSCRIPTION_TIERS[tier].period}` : SUBSCRIPTION_TIERS[tier].priceDisplay)
                    : undefined
                  }
                  accent={
                    // Both memberships, spelled out: a paid Plus member also
                    // holds the (free) Creator membership; a free creator sees
                    // what Creator alone includes (the upsell tile below sells
                    // the rest).
                    // Key off canUseTools, not the tier name: the free Creator/Reader
                    // tiers are status:active but not paid Plus, so they must never
                    // render as a "paid membership".
                    !canUseTools
                      ? 'Hey Joe Creator · includes selling & reader access'
                      : canBeReader ? 'Hey Joe Plus paid membership · Hey Joe Creator included (selling & reader access)' : undefined
                  }
                />
                <StatCard
                  label="Reader sessions"
                  value={`${readerSessionsRemaining} remaining`}
                  sub={`${readerSessionsUsed} of ${readerSessionsLimit} used ${isDayPass ? '' : 'this month'}`}
                />
                <StatCard
                  label={isDayPass ? 'Expires' : cancelAtPeriodEnd ? 'Access until' : 'Next billing'}
                  value={getEndDateDisplay()}
                  sub={cancelAtPeriodEnd && !isDayPass ? 'Subscription ending' : isDayPass ? '24-hour access' : undefined}
                  subTone={cancelAtPeriodEnd && !isDayPass ? 'warn' : undefined}
                />
              </div>

              {/* Free Creator tier: the play is the UPSELL to paid Plus — not a
                  pointless "cancel" on a $0 plan. */}
              {tier === 'creator' && !_subscription?.isUnlimited && (
                <div className="flex items-center justify-between gap-4 flex-wrap" style={{ marginTop: 18, padding: '14px 16px', borderRadius: 'var(--hj-r-md)', background: 'rgb(65 113 249 / 0.08)', border: '1px solid rgb(65 113 249 / 0.25)' }}>
                  <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
                    You're selling free — but your videos are working without the toolkit. <strong>Hey Joe Plus</strong> unlocks editing on every lesson (trim, audio cleanup, color, backgrounds), the full video tool suite, monthly video tokens for covers and enhancement, and member rates across the marketplace — $9.99/mo.
                  </p>
                  <Button onClick={() => startSubscription('monthly')} className="gap-2 flex-shrink-0">
                    Upgrade to Plus <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2.5" style={{ marginTop: 18 }}>
                {isDayPass ? (
                  <Button onClick={() => startSubscription('monthly')} className="gap-2">
                    <ArrowRight className="w-4 h-4" /> Upgrade to monthly
                  </Button>
                ) : cancelAtPeriodEnd ? (
                  <Button onClick={handleReactivateSubscription} disabled={actionLoading === 'reactivate'}>
                    {actionLoading === 'reactivate' ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Reactivating…</>
                    ) : 'Reactivate subscription'}
                  </Button>
                ) : tier !== 'creator' ? (
                  <Button variant="outline" onClick={() => setCancelOpen(true)} disabled={actionLoading === 'cancel'}>
                    {actionLoading === 'cancel' ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Canceling…</>
                    ) : 'Cancel subscription'}
                  </Button>
                ) : null}
                {/* The card on file. This card's own subtitle has always promised
                    "manage your subscription, payment, and renewal" while offering
                    no way to touch the payment — which is how members whose cards
                    expired got dunned out without ever being able to fix it. */}
                <Button variant="outline" onClick={() => navigate('/billing')} className="gap-2">
                  <CreditCard className="w-4 h-4" /> Update payment method
                </Button>
                {tier === 'monthly' && (
                  <Button variant="outline" onClick={() => activateCreator()} className="gap-2">
                    Start selling — free <ArrowRight className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </>
          ) : (
            <ChoosePlan startSubscription={startSubscription} activateCreator={activateCreator} subLoading={subLoading} />
          )}
        </section>

        {/* Charter member: permanent, its own row beneath the membership card so
            the status has a home that doesn't depend on a hover. */}
        {balance?.charterSince && (
          <div className="lg:col-span-2">
            <CharterMemberRow since={balance.charterSince} renewal={_subscription?.charterRenewal} />
          </div>
        )}

        {/* Referral link + Hey Joe credit balance. Renders nothing for people with
            neither a code (not a paid member) nor a balance. */}
        <div className="lg:col-span-2">
          <ReferralShareCard showBalance />
        </div>

        {/* Ticket Rewards — loyalty perk for members */}
        {isSubscriber && <TicketRewards />}

        {/* Credits Card — subscribers only */}
        {isSubscriber && (
          <section
            style={{
              padding: 24,
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
            }}
          >
            <div className="mono-label" style={{ marginBottom: 4 }}>Video tokens</div>
            <h2
              className="font-hj-display m-0"
              style={{
                fontSize: 22,
                color: 'var(--hj-ink)',
                letterSpacing: '-0.01em',
              }}
            >
              Video token balance
            </h2>
            <p
              className="m-0"
              style={{ marginTop: 4, color: 'var(--hj-ink-3)', fontSize: 13 }}
            >
              For Video Enhancer and AI features.
            </p>

            <div
              className="flex items-center gap-3"
              style={{
                marginTop: 18,
                padding: 18,
                borderRadius: 'var(--hj-r-sm)',
                background: 'var(--hj-blue-tint)',
                border: '1px solid var(--hj-blue)',
              }}
            >
              <Coins size={22} strokeWidth={1.8} style={{ color: 'var(--hj-yellow-ink)' }} />
              <div>
                <div className="mono-label" style={{ fontSize: 9 }}>Current balance</div>
                <div
                  className="tabular"
                  style={{
                    fontSize: 26,
                    fontWeight: 700,
                    color: 'var(--hj-ink)',
                    fontFamily: 'var(--hj-font-mono)',
                    lineHeight: 1.1,
                  }}
                >
                  {balance?.totalCredits ?? 0}
                </div>
              </div>
            </div>

            <div className="mono-label" style={{ marginTop: 18, marginBottom: 10 }}>
              Buy more video tokens
            </div>
            <div className="flex flex-col gap-2">
              {packages.map((pkg: CreditPackage) => (
                <button
                  key={pkg.packageId}
                  type="button"
                  onClick={() => handlePurchaseCredits(pkg.packageId)}
                  disabled={actionLoading === `purchase-${pkg.packageId}`}
                  className="flex items-center justify-between w-full transition-colors disabled:cursor-not-allowed"
                  style={{
                    padding: '12px 16px',
                    borderRadius: 999,
                    background: 'transparent',
                    border: '1px solid var(--hj-hairline)',
                    color: 'var(--hj-ink-2)',
                    fontSize: 13,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    minHeight: 44,
                  }}
                >
                  <span>{pkg.credits} video tokens</span>
                  <span
                    className="tabular"
                    style={{ color: 'var(--hj-ink)', fontWeight: 600, fontFamily: 'var(--hj-font-mono)' }}
                  >
                    {actionLoading === `purchase-${pkg.packageId}` ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      formatPrice(pkg.priceInCents)
                    )}
                  </span>
                </button>
              ))}
            </div>

            <p
              className="mono-label"
              style={{ marginTop: 14, fontSize: 9 }}
            >
              Video tokens never expire
            </p>
          </section>
        )}

        {/* Support Card */}
        <section
          style={{
            padding: 24,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          <div className="mono-label" style={{ marginBottom: 4 }}>Support</div>
          <h2
            className="font-hj-display m-0"
            style={{
              fontSize: 22,
              color: 'var(--hj-ink)',
              letterSpacing: '-0.01em',
            }}
          >
            Need a hand?
          </h2>
          <p
            className="m-0"
            style={{ marginTop: 4, color: 'var(--hj-ink-3)', fontSize: 13 }}
          >
            We respond to most messages within 24 hours.
          </p>

          <div className="flex flex-col gap-2" style={{ marginTop: 18 }}>
            <button
              type="button"
              onClick={openChatSupport}
              className="inline-flex items-center w-full justify-start gap-2"
              style={{
                padding: '12px 16px',
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--hj-hairline)',
                color: 'var(--hj-ink-2)',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              <MessageCircle size={14} strokeWidth={1.8} />
              Chat with support
            </button>
            <a
              href="mailto:hello@heyjoe.io"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center w-full justify-start gap-2"
              style={{
                padding: '12px 16px',
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--hj-hairline)',
                color: 'var(--hj-ink-2)',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'inherit',
                textDecoration: 'none',
                minHeight: 44,
              }}
            >
              <ExternalLink size={14} strokeWidth={1.8} />
              hello@heyjoe.io
            </a>
          </div>
        </section>

        {/* Discord community — auto role sync */}
        {isAuthenticated && (
          <section
            className="lg:col-span-2"
            style={{ padding: 24, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="mono-label" style={{ marginBottom: 4 }}>Community</div>
                <h2 className="font-hj-display m-0" style={{ fontSize: 22, color: 'var(--hj-ink)', letterSpacing: '-0.01em' }}>Discord</h2>
                <p className="m-0" style={{ marginTop: 4, color: 'var(--hj-ink-3)', fontSize: 13 }}>
                  Connect your account and your member, creator, and reader roles sync automatically.
                </p>
              </div>
              <MessageCircle size={22} strokeWidth={1.8} style={{ color: 'var(--hj-ink-3)', flexShrink: 0 }} />
            </div>

            {discord?.linked ? (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 14, color: 'var(--hj-ink)' }}>
                  Connected as <strong>@{discord.discordUsername || 'discord'}</strong>
                </div>
                <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 10 }}>
                  {([['member', 'Member'], ['creator', 'Creator'], ['reader', 'Reader']] as const).map(([k, label]) => {
                    const on = !!discord.roles?.[k];
                    return (
                      <span
                        key={k}
                        className="inline-flex items-center gap-1 mono-label"
                        style={{
                          padding: '3px 10px', borderRadius: 999, fontSize: 11,
                          border: `1px solid ${on ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
                          color: on ? 'var(--hj-blue)' : 'var(--hj-ink-4)',
                          background: on ? 'var(--hj-blue-tint)' : 'transparent',
                        }}
                      >
                        {on ? <CheckCircle size={11} /> : <XCircle size={11} />} {label}
                      </span>
                    );
                  })}
                  {!discord.roles?.member && !discord.roles?.creator && !discord.roles?.reader && (
                    <span style={{ fontSize: 12, color: 'var(--hj-ink-2)' }}>No roles yet — join Plus or become a reader/creator to unlock channels.</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 16 }}>
                  <Button variant="outline" size="sm" onClick={resyncDiscord} disabled={!!discordBusy}>
                    <RefreshCw size={14} strokeWidth={1.8} className="mr-1.5" />
                    {discordBusy === 'resync' ? 'Syncing…' : 'Re-sync roles'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={openCommunity}>
                    <ExternalLink size={14} strokeWidth={1.8} className="mr-1.5" /> Open Discord
                  </Button>
                  <Button variant="ghost" size="sm" onClick={unlinkDiscord} disabled={!!discordBusy} style={{ color: 'var(--hj-ink-4)' }}>
                    {discordBusy === 'unlink' ? 'Disconnecting…' : 'Disconnect'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 18 }}>
                {discord?.configured ? (
                  <Button onClick={connectDiscord} disabled={!!discordBusy}>
                    <MessageCircle size={15} strokeWidth={1.9} className="mr-1.5" />
                    {discordBusy === 'connect' ? 'Redirecting…' : 'Connect Discord'}
                  </Button>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--hj-ink-2)' }}>Account linking is coming soon — jump into the server in the meantime.</span>
                )}
                <button
                  type="button"
                  onClick={openCommunity}
                  className="inline-flex items-center gap-1.5"
                  style={{ padding: '9px 16px', borderRadius: 999, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-2)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}
                >
                  <ExternalLink size={14} strokeWidth={1.8} /> Open the Discord
                </button>
              </div>
            )}
          </section>
        )}
      </div>

      {/* Cancelling used to be a bare browser confirm() that named only what they'd
          keep until the period ended. Most people forget most of what a membership
          includes, so lay it out before they decide. */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        {/* Scrolls on a phone: the list made this taller than the screen, and with the
            footer stacked, "Cancel anyway" sat below the fold where nobody could reach it. */}
        <DialogContent className="sm:max-w-[460px] max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Before you cancel</DialogTitle>
            <DialogDescription>Here's everything your membership gets you.</DialogDescription>
          </DialogHeader>

          <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 12 }}>
            {[
              'Attend the free classes on our calendar and get member pricing on the paid ones.',
              'Book a reader for your next self-tape.',
              'Keep up to 10 videos on your casting profile instead of 1.',
              'Clean up your audition videos with the self-tape tools.',
              'A $25 Ticket Reward toward movies, theater, live events and sports every 4 months you stay a member.',
            ].map((f) => (
              <li key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.5, color: 'var(--hj-ink)' }}>
                <CheckCircle size={16} strokeWidth={1.8} style={{ color: 'var(--hj-green)', flexShrink: 0, marginTop: 2 }} />
                <span>{f}</span>
              </li>
            ))}
          </ul>


          <p style={{ margin: '16px 0 0', fontSize: 13.5, lineHeight: 1.5, color: 'var(--hj-ink)' }}>
            If you cancel, you keep all of it until {getEndDateDisplay()}.
          </p>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={handleCancelSubscription}>Cancel anyway</Button>
            <Button onClick={() => setCancelOpen(false)}>Never mind, keep it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: string | React.ReactNode;
  sub?: string;
  accent?: string;
  subTone?: 'warn';
}> = ({ label, value, sub, accent, subTone }) => {
  const subColor = subTone === 'warn' ? 'var(--hj-yellow)' : 'var(--hj-ink-3)';
  return (
    <div
      style={{
        padding: '14px 16px',
        borderRadius: 'var(--hj-r-sm)',
        background: 'var(--hj-surface-2)',
        border: '1px solid var(--hj-hairline-soft)',
      }}
    >
      <div className="mono-label" style={{ marginBottom: 6 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          color: 'var(--hj-ink)',
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ marginTop: 4, fontSize: 12, color: subColor }}>
          {sub}
        </div>
      )}
      {accent && (
        <div className="mono-label" style={{ marginTop: 6, color: 'var(--hj-yellow-ink)' }}>
          {accent}
        </div>
      )}
    </div>
  );
};

const ChoosePlan: React.FC<{
  startSubscription: (tier: 'day_pass' | 'monthly' | 'creator' | 'reader') => void;
  activateCreator: () => Promise<boolean>;
  subLoading: boolean;
}> = ({ startSubscription, activateCreator, subLoading }) => {
  return (
    <div style={{ marginTop: 22 }}>
      <div className="text-center" style={{ marginBottom: 18 }}>
        <div className="mono-label" style={{ marginBottom: 4 }}>Choose your plan</div>
        <p
          className="m-0"
          style={{ color: 'var(--hj-ink-3)', fontSize: 13 }}
        >
          Unlock Hey Joe+ features.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <PlanCard
          tierKey="day_pass"
          ctaLabel="Get day pass"
          variant="ghost"
          onClick={() => startSubscription('day_pass')}
          disabled={subLoading}
        />
        <PlanCard
          tierKey="monthly"
          ctaLabel="Subscribe"
          variant="primary"
          highlighted
          onClick={() => startSubscription('monthly')}
          disabled={subLoading}
        />
        <PlanCard
          tierKey="creator"
          ctaLabel="Start selling — Free"
          variant="ghost"
          onClick={() => activateCreator()}
          disabled={subLoading}
        />
      </div>
    </div>
  );
};

const PlanCard: React.FC<{
  tierKey: 'day_pass' | 'monthly' | 'creator';
  ctaLabel: string;
  variant: 'primary' | 'ghost';
  highlighted?: boolean;
  onClick: () => void;
  disabled?: boolean;
}> = ({ tierKey, ctaLabel, variant, highlighted, onClick, disabled }) => {
  const t = SUBSCRIPTION_TIERS[tierKey];
  if (!t) return null;
  return (
    <div
      className="relative flex flex-col"
      style={{
        padding: 18,
        borderRadius: 'var(--hj-r-md)',
        background: highlighted ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
        border: `1px solid ${highlighted ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
      }}
    >
      {highlighted && (
        <span
          className="mono-label absolute"
          style={{
            top: -10,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '3px 10px',
            borderRadius: 999,
            background: 'var(--hj-blue)',
            color: 'oklch(0.18 0.005 70)',
            fontSize: 9,
            fontWeight: 700,
          }}
        >
          Most popular
        </span>
      )}
      <div className="text-center" style={{ marginTop: highlighted ? 6 : 0 }}>
        <div
          className="tabular"
          style={{
            fontSize: 26,
            fontWeight: 700,
            color: 'var(--hj-ink)',
            fontFamily: 'var(--hj-font-mono)',
            lineHeight: 1,
          }}
        >
          {t.priceDisplay}
        </div>
        <div
          className="mono-label"
          style={{ marginTop: 6 }}
        >
          {t.period}
        </div>
      </div>
      <ul
        className="m-0"
        style={{
          marginTop: 14,
          padding: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          flex: 1,
        }}
      >
        {t.features.map((f, i) => (
          <li
            key={i}
            className="flex items-start gap-2"
            style={{ fontSize: 12.5, color: 'var(--hj-ink-2)' }}
          >
            <CheckCircle
              size={12}
              strokeWidth={2.2}
              style={{ color: 'var(--hj-green)', flexShrink: 0, marginTop: 4 }}
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        variant={variant === 'primary' ? 'default' : 'outline'}
        className="w-full"
        style={{ marginTop: 14 }}
        onClick={onClick}
        disabled={disabled}
      >
        {ctaLabel}
      </Button>
    </div>
  );
};

export default AccountPage;